import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import Navbar from "@/components/navbar";
import { useAuth } from "@/hooks/useAuthState";
import { useToast } from "@/hooks/use-toast";
// COMMENTED OUT: ID Verification disabled
// import { IDVerificationPopup } from "@/components/id-verification-popup";
import { CountdownTimer } from "@/components/challenges/CountdownTimer";
import { 
  Target, 
  Trophy, 
  Users, 
  ArrowRight,
  Gift,
  Clock,
  CheckCircle,
  Loader2,
  Star
} from "lucide-react";

interface Challenge {
  id: string;
  name: string;
  description?: string;
  type: 'acquisition' | 'participation' | 'growth' | 'marketing';
  status: 'draft' | 'live' | 'ended' | 'scheduled';
  startDateTime?: string;
  targetValue: number;
  prizeDetails?: {
    '1st'?: { item: string; value: number };
    '2nd'?: { item: string; value: number };
    '3rd'?: { item: string; value: number };
  };
  totalParticipants: number;
  userProgress?: {
    joined: boolean;
    progressCount: number;
    rank?: number;
  };
}

function ChallengeCard({ challenge, onJoin }: { 
  challenge: Challenge; 
  onJoin: (challengeId: string) => void; 
}) {
  const { user } = useAuth();
  const [isJoining, setIsJoining] = useState(false);

  const hasJoined = challenge.userProgress?.joined;
  const progress = challenge.userProgress?.progressCount || 0;
  const progressPercentage = Math.min((progress / challenge.targetValue) * 100, 100);

  const handleCardClick = async () => {
    console.log(`[MOBILE CHALLENGE CARD] Clicked challenge ${challenge.id} with status: ${challenge.status}`);
    
    // Prevent action if challenge has ended or is scheduled
    if (challenge.status === 'ended' || challenge.status === 'scheduled') {
      console.log(`[MOBILE CHALLENGE CARD] Prevented action on ${challenge.status} challenge`);
      return;
    }

    if (hasJoined) {
      // Navigate to challenge detail page
      window.location.href = `/m/challenges/${challenge.id}`;
      return;
    }

    if (user) {
      setIsJoining(true);
      await onJoin(challenge.id);
      setIsJoining(false);
    } else {
      // Navigate to challenge detail for signup
      window.location.href = `/m/challenges/${challenge.id}`;
    }
  };

  const isEnded = challenge.status === 'ended';
  const isScheduled = challenge.status === 'scheduled';

  return (
    <Card 
      className={`transition-shadow duration-200 border-2 ${
        isEnded || isScheduled
          ? 'opacity-60 cursor-not-allowed border-gray-300' 
          : 'hover:shadow-md hover:border-purple-200 cursor-pointer'
      }`}
      onClick={handleCardClick}
      data-testid={`card-challenge-${challenge.id}`}
    >
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 pr-3">
            <div className="flex items-center space-x-2 mb-1">
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg p-1">
                <Target className="h-4 w-4 text-white" />
              </div>
              <div className="flex flex-col gap-1">
                <Badge 
                  variant={challenge.status === 'live' ? 'default' : 'secondary'} 
                  className={`text-xs ${
                    challenge.status === 'live' ? 'bg-green-600' : 
                    challenge.status === 'ended' ? 'bg-gray-500 text-white' :
                    challenge.status === 'scheduled' ? 'bg-orange-500 text-white' : ''
                  }`}
                >
                  {challenge.status === 'live' ? '🔴 Live' : 
                   challenge.status === 'ended' ? '⏹️ Ended' : 
                   challenge.status === 'scheduled' ? '⏰ Starting Soon' : 
                   challenge.status}
                </Badge>
                {isScheduled && challenge.startDateTime && (
                  <CountdownTimer targetDate={challenge.startDateTime} />
                )}
              </div>
            </div>
            <h3 className="font-semibold text-sm line-clamp-2 mb-1">
              {challenge.name}
            </h3>
            {challenge.description && (
              <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                {challenge.description}
              </p>
            )}
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-500">Target</div>
            <div className="text-lg font-bold text-purple-600">
              {challenge.targetValue}
            </div>
          </div>
        </div>

        {/* Progress Section (if joined) */}
        {hasJoined && (
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                Your Progress
              </span>
              {challenge.userProgress?.rank && (
                <Badge variant="outline" className="text-xs">
                  Rank #{challenge.userProgress.rank}
                </Badge>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span>{progress} referrals</span>
                <span>{challenge.targetValue} goal</span>
              </div>
              <Progress value={progressPercentage} className="h-2" />
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {progressPercentage.toFixed(1)}% complete
              </div>
            </div>
          </div>
        )}

        {/* Prize Section */}
        {challenge.prizeDetails && Object.keys(challenge.prizeDetails).length > 0 && (
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-lg p-3 mb-3">
            <div className="flex items-center mb-2">
              <Trophy className="h-4 w-4 text-yellow-600 mr-2" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">Prizes</span>
            </div>
            <div className="space-y-1">
              {challenge.prizeDetails?.['1st'] && (
                <div className="text-xs">
                  🥇 1st: {challenge.prizeDetails['1st'].item}
                </div>
              )}
              {challenge.prizeDetails?.['2nd'] && (
                <div className="text-xs">
                  🥈 2nd: {challenge.prizeDetails['2nd'].item}
                </div>
              )}
              {challenge.prizeDetails?.['3rd'] && (
                <div className="text-xs">
                  🥉 3rd: {challenge.prizeDetails['3rd'].item}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stats and Action */}
        <div className="space-y-3">
          <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
            <span className="flex items-center">
              <Users className="h-3 w-3 mr-1" />
              {challenge.totalParticipants} Participants
            </span>
            <span className="flex items-center">
              <Target className="h-3 w-3 mr-1" />
              Referral Challenge
            </span>
          </div>

          <Button 
            className={`w-full h-12 transition-colors ${
              isEnded || isScheduled
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : hasJoined 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-purple-600 hover:bg-purple-700'
            }`}
            disabled={isJoining || isEnded || isScheduled}
            data-testid={`button-${isEnded ? 'ended' : isScheduled ? 'starting-soon' : hasJoined ? 'view' : 'join'}-challenge-${challenge.id}`}
          >
            {isEnded ? (
              <div className="flex items-center justify-center space-x-2">
                <span>Challenge Ended</span>
              </div>
            ) : isScheduled ? (
              <div className="flex items-center justify-center space-x-2">
                <span>Starting Soon</span>
              </div>
            ) : isJoining ? (
              <div className="flex items-center justify-center space-x-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Joining...</span>
              </div>
            ) : hasJoined ? (
              <div className="flex items-center justify-center space-x-2">
                <CheckCircle className="h-4 w-4" />
                <span>{(challenge.status === 'live' || challenge.status === 'active') ? 'Entered' : 'Joined'}</span>
                <ArrowRight className="h-4 w-4" />
              </div>
            ) : user ? (
              <div className="flex items-center justify-center space-x-2">
                <Trophy className="h-4 w-4" />
                <span>Join Challenge</span>
                <ArrowRight className="h-4 w-4" />
              </div>
            ) : (
              <div className="flex items-center justify-center space-x-2">
                <Target className="h-4 w-4" />
                <span>View Challenge</span>
                <ArrowRight className="h-4 w-4" />
              </div>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ChallengesSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <Card key={i}>
          <CardContent className="p-4 space-y-3">
            <div className="flex justify-between items-start">
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-full" />
              </div>
              <Skeleton className="h-8 w-12" />
            </div>
            <Skeleton className="h-16 w-full rounded-lg" />
            <Skeleton className="h-12 w-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function MobileChallenges() {
  const { user, session } = useAuth();
  const { toast } = useToast();
  // COMMENTED OUT: ID Verification disabled
  // const [showIdVerification, setShowIdVerification] = useState(false);
  // const [selectedChallenge, setSelectedChallenge] = useState<Challenge | null>(null);
  const [isJoining, setIsJoining] = useState<string | null>(null);

  // Fetch active challenges with user progress
  const { data: challengeData, isLoading, error, refetch } = useQuery({
    queryKey: ['challenges', 'active', user?.id],
    queryFn: async () => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };

      // Add auth header if user is authenticated
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch('/api/v1/challenges/active', {
        headers
      });

      if (!response.ok) {
        throw new Error('Failed to fetch challenges');
      }
      return response.json();
    },
    enabled: true // Always fetch, but include auth when available
  });

  // Handle joining a challenge with mandatory ID verification
  const handleJoinChallenge = async (challengeId: string) => {
    // Check if challenge has ended
    const challenge = challenges.find(c => c.id === challengeId);
    if (challenge?.status === 'ended') {
      toast({
        title: "Challenge Ended",
        description: "This challenge has ended and is no longer accepting participants.",
      });
      return;
    }

    if (!user || !session?.access_token) {
      // Redirect to auth page
      window.location.href = `/auth?intent=join&challengeId=${challengeId}`;
      return;
    }

    setIsJoining(challengeId);

    try {
      const response = await fetch(`/api/v1/challenges/${challengeId}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        toast({
          title: "🎉 Challenge Joined!",
          description: "You've successfully joined the challenge. Redirecting...",
        });

        // COMMENTED OUT: ID Verification disabled
        // // After successful join, check ID verification status
        // try {
        //   console.log('[MOBILE JOIN] Successfully joined challenge, now checking ID verification status');
        //   const userResponse = await fetch(`/api/auth/user`, {
        //     headers: {
        //       'Authorization': `Bearer ${session.access_token}`
        //     }
        //   });

        //   if (userResponse.ok) {
        //     const userData = await userResponse.json();
        //     if (!userData.idVerified) {
        //       console.log('[MOBILE JOIN] User not verified after joining - showing ID verification popup');
        //       const challenge = challenges.find(c => c.id === challengeId);
        //       setSelectedChallenge(challenge || null);
        //       setShowIdVerification(true);
        //       setIsJoining(null);
        //       return;
        //     } else {
        //       console.log('[MOBILE JOIN] User already verified, proceeding to challenge page');
        //     }
        //   } else {
        //     console.log('[MOBILE JOIN] Cannot check verification status after join, showing popup for safety');
        //     const challenge = challenges.find(c => c.id === challengeId);
        //     setSelectedChallenge(challenge || null);
        //     setShowIdVerification(true);
        //     setIsJoining(null);
        //     return;
        //   }
        // } catch (verificationError) {
        //   console.error('[MOBILE JOIN] Error checking verification after join:', verificationError);
        //   // Show verification popup if we can't check status for security
        //   const challenge = challenges.find(c => c.id === challengeId);
        //   setSelectedChallenge(challenge || null);
        //   setShowIdVerification(true);
        //   setIsJoining(null);
        //   return;
        // }

        // Refresh challenge data
        refetch();

        // Navigate to challenge detail page
        setTimeout(() => {
          window.location.href = `/m/challenges/${challengeId}`;
        }, 1500);
      } else {
        const errorData = await response.json();

        if (errorData.error?.includes('Already joined')) {
          toast({
            title: "Already Joined",
            description: "You're already participating in this challenge.",
          });
          setTimeout(() => {
            window.location.href = `/m/challenges/${challengeId}`;
          }, 1000);
        } else {
          throw new Error(errorData.error || 'Failed to join challenge');
        }
      }
    } catch (error) {
      console.error('Failed to join challenge:', error);
      toast({
        title: "Join Failed",
        description: error instanceof Error ? error.message : 'Failed to join challenge. Please try again.',
        variant: "destructive",
      });
    } finally {
      setIsJoining(null);
    }
  };

  // COMMENTED OUT: ID Verification disabled
  // // Handle successful ID verification
  // const handleIdVerificationSuccess = async () => {
  //   console.log('[MOBILE ID_VERIFICATION] ID verification successful');
  //   
  //   const challengeId = selectedChallenge?.id;
  //   
  //   setShowIdVerification(false);

  //   toast({
  //     title: "ID Verification Complete",
  //     description: "Your identity has been verified successfully! Redirecting...",
  //   });

  //   // Refetch challenge data to update the "Joined" status immediately
  //   await refetch();
  //   
  //   // Clear selected challenge after refetch so UI updates
  //   setSelectedChallenge(null);

  //   // Redirect to the challenge dashboard page
  //   if (challengeId) {
  //     setTimeout(() => {
  //       window.location.href = `/m/challenges/${challengeId}`;
  //     }, 800);
  //   }
  // };

  // Process challenge data and filter out ended challenges
  const challenges: Challenge[] = challengeData?.challenges ? challengeData.challenges
    .filter((challenge: any) => {
      // Filter out ended challenges on the frontend as well
      const isEnded = challenge.status === 'ended';
      if (isEnded) {
        console.log(`[MOBILE CHALLENGES] Filtering out ended challenge: ${challenge.name}`);
      }
      return !isEnded;
    })
    .map((challenge: any) => ({
      ...challenge,
      id: challenge.id?.toString() || '1',
      targetValue: challenge.targetValue || 1000,
      totalParticipants: challenge.totalParticipants || 0,
      prizeDetails: convertPrizesToPrizeDetails(challenge.prizes),
      status: challenge.status || 'live',
      type: challenge.type || 'acquisition' as const,
      userProgress: challenge.userProgress ? {
        joined: true,
        progressCount: challenge.userProgress.currentValue || 0,
        rank: challenge.userProgress.rank
      } : undefined
    })) : [];

  // Debug challenge data
  console.log('[MOBILE CHALLENGES] Challenge data from API:', challengeData);
  console.log('[MOBILE CHALLENGES] Processed active challenges:', challenges);

  // Helper function to convert API prizes format to component format
  function convertPrizesToPrizeDetails(prizes: any) {
    if (!prizes) return undefined;

    const prizeDetails: any = {};

    if (prizes.prize1) {
      prizeDetails['1st'] = {
        item: prizes.prize1,
        value: 0
      };
    }

    if (prizes.prize2) {
      prizeDetails['2nd'] = {
        item: prizes.prize2,
        value: 0
      };
    }

    if (prizes.prize3) {
      prizeDetails['3rd'] = {
        item: prizes.prize3,
        value: 0
      };
    }

    return Object.keys(prizeDetails).length > 0 ? prizeDetails : undefined;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24 overflow-x-hidden">
        <div className="px-4 py-16 text-center">
          <Target className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Failed to Load Challenges
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Unable to fetch challenges. Please try again.
          </p>
          <Button onClick={() => refetch()} data-testid="button-retry">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24 overflow-x-hidden">
      {/* Show Navbar for logged-out users */}
      {!user && <Navbar />}
      {/* Mobile Header */}
      <div className="text-white" style={{ background: 'linear-gradient(to right, #0e1b90, #1e2bb0)' }}>
        <div className="px-4 pt-12 pb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3 flex-1">
              <div>
                <h1 className="text-2xl font-bold">Challenges</h1>
                <p className="text-blue-100 text-sm">Refer friends and win prizes</p>
              </div>
            </div>
            <div className="bg-white/20 rounded-full p-3">
              <Target className="h-6 w-6" />
            </div>
          </div>

          {/* Stats */}
          <div className="bg-white/20 rounded-lg p-3">
            <div className="text-center">
              <div className="text-lg font-bold">
                {challenges.length} Active Challenge{challenges.length !== 1 ? 's' : ''}
              </div>
              <div className="text-xs text-blue-100">
                Join the competition and start referring!
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6">
        {isLoading ? (
          <ChallengesSkeleton />
        ) : challenges.length === 0 ? (
          <div className="text-center py-16">
            <Target className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              No Challenges Available
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              There are currently no challenges available. Check back soon for new competitions!
            </p>
            <Link href="/m/competitions">
              <Button data-testid="button-view-competitions">
                View Competitions Instead
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {challenges.map((challenge) => (
              <ChallengeCard
                key={challenge.id}
                challenge={challenge}
                onJoin={handleJoinChallenge}
              />
            ))}
          </div>
        )}
      </div>


      {/* COMMENTED OUT: ID Verification disabled */}
      {/* {/* ID Verification Popup */}
      {/* <IDVerificationPopup
        isOpen={showIdVerification}
        onClose={() => {
          setShowIdVerification(false);
          setSelectedChallenge(null);
        }}
        onVerified={handleIdVerificationSuccess}
        challengeName={selectedChallenge?.name || 'Challenge'}
      /> */}
    </div>
  );
}